import React, { createContext, useContext, useState, useEffect } from 'react';

const DashboardContext = createContext(null);

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

export const DashboardProvider = ({ children }) => {
    const [cachedData, setCachedData] = useState({
        userPools: null,
        singlesRankings: null,
        doublesRankings: null,
        userSinglesMatches: null,
        userDoublesMatches: null,
        userRecentSinglesMatches: null, 
        userRecentDoublesMatches: null, 
        userProfile: null,
    });

    // Load initial cache from localStorage (per-resource)
    useEffect(() => {
        function getCache(key) {
            const raw = localStorage.getItem(key);
            if (!raw) return { data: null, lastFetched: null };
            try {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === 'object' && 'data' in parsed && 'lastFetched' in parsed) {
                    return parsed;
                } else {
                    // Backward compatibility: treat as data only
                    return { data: parsed, lastFetched: null };
                }
            } catch {
                return { data: null, lastFetched: null };
            }
        }
        setCachedData({
            userPools: getCache('userPools').data,
            singlesRankings: getCache('singlesRankings').data,
            doublesRankings: getCache('doublesRankings').data,
            userSinglesMatches: getCache('userSinglesMatches').data,
            userDoublesMatches: getCache('userDoublesMatches').data,
            userRecentSinglesMatches: getCache('userRecentSinglesMatches').data, 
            userRecentDoublesMatches: getCache('userRecentDoublesMatches').data, 
            userProfile: getCache('userProfile').data,
        });
    }, []);

    // Helper to set cache for a key
    function setCache(key, data) {
        localStorage.setItem(key, JSON.stringify({ data, lastFetched: Date.now() }));
    }

    const updateCache = (newData) => {
        // Update state and localStorage for each key in newData
        const updated = { ...cachedData };
        for (const key of Object.keys(newData)) {
            updated[key] = newData[key];
            setCache(key, newData[key]);
        }
        setCachedData(updated);
    };

    // Helper function to update rankings cache with new points
    const updateRankingsCache = (matchType, userId, pointsChange) => {
        const rankingsCacheKey = matchType === 'singles' ? 'singlesRankings' : 'doublesRankings';
        const rankingsCacheObj = JSON.parse(localStorage.getItem(rankingsCacheKey) || '{}');
        const rankingsCache = rankingsCacheObj && rankingsCacheObj.data ? rankingsCacheObj.data : rankingsCacheObj;
        const lastFetched = rankingsCacheObj.lastFetched || null;
        
        if (!rankingsCache || !rankingsCache.rankings) return;

        // Only update if the player exists in the rankings cache
        const existingPlayer = rankingsCache.rankings.find(p => p.userId === userId);
        if (existingPlayer) {
            const updatedRankings = rankingsCache.rankings.map(player => {
                if (player.userId === userId) {
                    return {
                        ...player,
                        [matchType === 'singles' ? 'singlesPoints' : 'doublesPoints']: 
                            (player[matchType === 'singles' ? 'singlesPoints' : 'doublesPoints'] || 0) + pointsChange
                    };
                }
                return player;
            });
            // Sort by points (highest first)
            updatedRankings.sort((a, b) => 
                b[matchType === 'singles' ? 'singlesPoints' : 'doublesPoints'] - 
                a[matchType === 'singles' ? 'singlesPoints' : 'doublesPoints']
            );
            rankingsCache.rankings = updatedRankings;
            localStorage.setItem(rankingsCacheKey, JSON.stringify({ data: rankingsCache, lastFetched }));
        }
        // If the player doesn't exist in the rankings cache, do nothing
    };

    /**
     * Adds a match to the specified cache (user or all) only if the match's location matches the cache's locationString.
     * @param {string} cacheKey - The cache key (e.g., 'userSinglesMatches', 'allDoublesMatches')
     * @param {object} match - The match object to add
     */
    const addMatchToCacheIfLocationMatches = (cacheKey, match) => {
        const cachedObj = JSON.parse(localStorage.getItem(cacheKey) || '{}');
        const cached = cachedObj && cachedObj.data ? cachedObj.data : cachedObj;
        const lastFetched = cachedObj.lastFetched || null;
        const locationString = cached.locationString;
        if (!locationString) return; // Don't add if locationString is not set (matches not fetched yet)
        if (!match.location || !match.location.startsWith(locationString)) return; // Only add if match location matches
        const existingMatches = cached.matches || [];
        const updatedMatches = [match, ...existingMatches];
        // Preserve all other properties (like lastEvaluatedKey, etc.)
        const { matches, partitions, ...rest } = cached;
        localStorage.setItem(cacheKey, JSON.stringify({ data: { ...rest, matches: updatedMatches }, lastFetched }));
        // Optionally: could update state if needed
    };

    /**
     * Adds a match to the user's recent matches cache (no location check)
     * @param {'singles'|'doubles'} matchType
     * @param {object} match
     */
    const addMatchToRecentCache = (matchType, match) => {
        const cacheKey = matchType === 'singles' ? 'userRecentSinglesMatches' : 'userRecentDoublesMatches';
        const cachedObj = JSON.parse(localStorage.getItem(cacheKey) || '{}');
        const cached = cachedObj && cachedObj.data ? cachedObj.data : cachedObj;
        const lastFetched = cachedObj.lastFetched || null;
        const existingMatches = cached.matches || [];
        const updatedMatches = [match, ...existingMatches];
        // Preserve all other properties (like lastEvaluatedKey, etc.)
        const { matches, partitions, ...rest } = cached;
        localStorage.setItem(cacheKey, JSON.stringify({ data: { ...rest, matches: updatedMatches }, lastFetched }));
    };

    /**
     * Removes a match from the user's recent matches cache by timestamp
     * @param {'singles'|'doubles'} matchType
     * @param {number} timestamp
     */
    const removeMatchFromRecentCache = (matchType, timestamp) => {
        const cacheKey = matchType === 'singles' ? 'userRecentSinglesMatches' : 'userRecentDoublesMatches';
        const cachedObj = JSON.parse(localStorage.getItem(cacheKey) || '{}');
        const cached = cachedObj && cachedObj.data ? cachedObj.data : cachedObj;
        const lastFetched = cachedObj.lastFetched || null;
        const existingMatches = cached.matches || [];
        const updatedMatches = existingMatches.filter(m => m.timestamp !== timestamp);
        // Preserve all other properties (like lastEvaluatedKey, etc.)
        const { matches, partitions, ...rest } = cached;
        localStorage.setItem(cacheKey, JSON.stringify({ data: { ...rest, matches: updatedMatches }, lastFetched }));
    };

    // Helper function to remove match from user matches cache (unified structure)
    const removeMatchFromUserCache = (matchType, timestamp) => {
        const cacheKey = matchType === 'singles' ? 'userSinglesMatches' : 'userDoublesMatches';
        const cachedObj = JSON.parse(localStorage.getItem(cacheKey) || '{}');
        const cached = cachedObj && cachedObj.data ? cachedObj.data : cachedObj;
        const lastFetched = cachedObj.lastFetched || null;
        const existingMatches = cached.matches || [];
        const updatedMatches = existingMatches.filter(m => m.timestamp !== timestamp);
        // Preserve all other properties (like lastEvaluatedKey, locationString)
        const { matches, partitions, ...rest } = cached;
        localStorage.setItem(cacheKey, JSON.stringify({ data: { ...rest, matches: updatedMatches }, lastFetched }));
        console.debug(`[DashboardContext] removeMatchFromUserCache ${matchType}:`, { updatedMatches, ...rest });
    };

    // Helper function to remove match from all matches cache (unified structure)
    const removeMatchFromAllCache = (matchType, timestamp) => {
        const cacheKey = matchType === 'singles' ? 'allSinglesMatches' : 'allDoublesMatches';
        const cachedObj = JSON.parse(localStorage.getItem(cacheKey) || '{}');
        const cached = cachedObj && cachedObj.data ? cachedObj.data : cachedObj;
        const lastFetched = cachedObj.lastFetched || null;
        const existingMatches = cached.matches || [];
        const updatedMatches = existingMatches.filter(m => m.timestamp !== timestamp);
        // Preserve all other properties (like lastEvaluatedKey, locationString)
        const { matches, partitions, ...rest } = cached;
        localStorage.setItem(cacheKey, JSON.stringify({ data: { ...rest, matches: updatedMatches }, lastFetched }));
        console.debug(`[DashboardContext] removeMatchFromAllCache ${matchType}:`, { updatedMatches, ...rest });
    };

    /**
     * Marks a match as confirmed in all relevant caches (recent, user, all) for the given matchType.
     * @param {'singles'|'doubles'} matchType
     * @param {number} timestamp
     * @param {string} location
     */
    const matchCacheKeys = [
      'userRecentSinglesMatches', 'userRecentDoublesMatches',
      'userSinglesMatches', 'userDoublesMatches',
      'allSinglesMatches', 'allDoublesMatches'
    ];
    const markMatchConfirmedInCaches = (matchType, timestamp, location) => {
      matchCacheKeys.forEach(cacheKey => {
        // Only update relevant caches for the matchType
        if (
          (matchType === 'singles' && cacheKey.includes('Singles')) ||
          (matchType === 'doubles' && cacheKey.includes('Doubles'))
        ) {
          const cachedObj = JSON.parse(localStorage.getItem(cacheKey) || '{}');
          const cached = cachedObj && cachedObj.data ? cachedObj.data : cachedObj;
          const lastFetched = cachedObj.lastFetched || null;
          const existingMatches = cached.matches || [];
          const updatedMatches = existingMatches.map(m =>
            m.timestamp === timestamp && m.location === location
              ? { ...m, confirmed: true }
              : m
          );
          const { matches, partitions, ...rest } = cached;
          localStorage.setItem(cacheKey, JSON.stringify({ data: { ...rest, matches: updatedMatches }, lastFetched }));
        }
      });
    };

    const clearCache = () => {
        // Clear state
        setCachedData({
            userPools: null,
            singlesRankings: null,
            doublesRankings: null,
            userSinglesMatches: null,
            userDoublesMatches: null,
            userRecentSinglesMatches: null, 
            userRecentDoublesMatches: null, 
            userProfile: null,
        });

        // Clear localStorage
        localStorage.clear();
    };

    // Per-resource cache validity check
    const isCacheValid = (lastFetched) => {
        if (!lastFetched) return false;
        const now = Date.now();
        return (now - lastFetched) < CACHE_DURATION;
    };

    return (
        <DashboardContext.Provider value={{ 
            cachedData, 
            updateCache, 
            clearCache,
            isCacheValid,
            CACHE_DURATION,
            updateRankingsCache,
            addMatchToCacheIfLocationMatches,
            addMatchToRecentCache, 
            removeMatchFromRecentCache, 
            removeMatchFromUserCache,
            removeMatchFromAllCache,
            markMatchConfirmedInCaches
        }}>
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error('useDashboard must be used within a DashboardProvider');
    }
    return context;
};